/**
 * The accessible text summary of a chart: its title plus its values, as one string for `aria-label` (or a visually
 * hidden description). It is locale-NEUTRAL by construction: the library contains no words, no digits formatting and no
 * currency — the caller injects `format` (and, for shares, `formatShare`) and every separator it wants. A tooltip is never
 * the only place a value lives; this string always has them.
 */
import { isFiniteNumber, seriesStats } from './numbers.js';
function joinTitle(title, body, separator) {
    if (title === undefined || title === '')
        return body;
    return body === '' ? title : `${title}${separator}${body}`;
}
/** The indices to list when the series is longer than `max`: always first, last, min and max, then an even spread. */
export function sampleIndices(values, max) {
    const length = values.length;
    const cap = Math.max(1, Math.floor(max));
    if (length <= cap)
        return values.map((_, i) => i);
    const picked = new Set([0, length - 1]);
    const stats = seriesStats(values);
    if (stats !== null) {
        picked.add(stats.minIndex);
        picked.add(stats.maxIndex);
    }
    const required = [...picked];
    // Never exceed the cap even if the four required indices alone do.
    const keep = new Set(required.slice(0, cap));
    const free = cap - keep.size;
    if (free > 0) {
        const stride = length / (free + 1);
        for (let k = 1; k <= free; k += 1) {
            let i = Math.round(k * stride);
            while (keep.has(i) && i < length - 1)
                i += 1;
            keep.add(Math.min(i, length - 1));
        }
    }
    return [...keep].sort((a, b) => a - b);
}
/** A series (line, columns, bars): `Title: label: value; label: value`. */
export function buildSeriesSummary(items, options) {
    const pair = options.pairSeparator ?? ': ';
    const sep = options.itemSeparator ?? '; ';
    const missing = options.missingText ?? '–';
    if (items.length === 0)
        return joinTitle(options.title, options.emptyText ?? '', options.titleSeparator ?? ': ');
    const idx = sampleIndices(items.map((i) => i.value), options.maxItems ?? 12);
    const body = idx
        .map((i) => {
        const item = items[i];
        return `${item.label}${pair}${isFiniteNumber(item.value) ? options.format(item.value) : missing}`;
    })
        .join(sep);
    return joinTitle(options.title, body, options.titleSeparator ?? ': ');
}
/** Parts of a whole (donut, status split): `Title: label: value (share); …`. Slices keep their order; none is sampled away. */
export function buildSharesSummary(items, options) {
    const pair = options.pairSeparator ?? ': ';
    const sep = options.itemSeparator ?? '; ';
    const open = options.shareOpen ?? ' (';
    const close = options.shareClose ?? ')';
    const total = items.reduce((sum, i) => sum + (isFiniteNumber(i.value) && i.value > 0 ? i.value : 0), 0);
    if (items.length === 0)
        return joinTitle(options.title, options.emptyText ?? '', options.titleSeparator ?? ': ');
    const body = items
        .map((item) => {
        const value = isFiniteNumber(item.value) ? item.value : 0;
        const share = options.formatShare !== undefined && total > 0 ? `${open}${options.formatShare(Math.max(0, value) / total)}${close}` : '';
        return `${item.label}${pair}${options.format(value)}${share}`;
    })
        .join(sep);
    return joinTitle(options.title, body, options.titleSeparator ?? ': ');
}
/** A single figure against its whole (ring, meter): `Label: 40 / 100`. */
export function buildRatioSummary(input) {
    const figure = isFiniteNumber(input.max) ? `${input.format(input.value)}${input.ofSeparator ?? ' / '}${input.format(input.max)}` : input.format(input.value);
    const body = input.suffix === undefined || input.suffix === '' ? figure : `${figure} ${input.suffix}`;
    return joinTitle(input.label, body, input.pairSeparator ?? ': ');
}
//# sourceMappingURL=summary.js.map